import React, { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertTriangle, CheckCircle, Clock, Zap, RefreshCw, FileWarning, Volume2 } from 'lucide-react';
import { useContentQuality } from '@/hooks/useContentQuality';
import { useToast } from '@/hooks/use-toast';

export const ContentQualityDashboard: React.FC = () => {
  const { 
    report, 
    isAnalyzing, 
    isFixing,
    lastAnalyzed, 
    errors, 
    canManageContent,
    analyzeContentQuality,
    fixCorruptedContent
  } = useContentQuality();
  
  const { toast } = useToast();

  useEffect(() => {
    if (canManageContent && !report) {
      analyzeContentQuality();
    }
  }, [canManageContent, report, analyzeContentQuality]);

  const handleAutoFix = async () => {
    try {
      const result = await fixCorruptedContent();
      toast({
        title: "Content Fix Triggered",
        description: `Processing ${result?.totalCorrupted || 0} corrupted modules`,
      });
    } catch (error) {
      toast({
        title: "Auto-fix Failed",
        description: error instanceof Error ? error.message : "Failed to apply fixes",
        variant: "destructive",
      });
    }
  };

  if (!canManageContent) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">
            Content quality management requires admin or staff permissions.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (isAnalyzing && !report) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center space-x-2">
            <RefreshCw className="h-4 w-4 animate-spin" />
            <span>Analyzing content quality...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (errors.length > 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center space-x-2 text-red-600">
            <AlertTriangle className="h-4 w-4" />
            <span>Analysis Error: {errors[0]}</span>
          </div>
          <Button 
            onClick={analyzeContentQuality} 
            className="mt-4"
            variant="outline"
          >
            Retry Analysis
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Content Quality Dashboard</h2>
          {lastAnalyzed && (
            <p className="text-sm text-muted-foreground">
              Last analyzed: {lastAnalyzed.toLocaleString()}
            </p>
          )}
        </div>
        <div className="flex space-x-2">
          <Button 
            onClick={analyzeContentQuality}
            disabled={isAnalyzing}
            variant="outline"
          >
            {isAnalyzing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
            Refresh Analysis
          </Button>
          {report?.corruptedCount > 0 && (
            <Button 
              onClick={handleAutoFix}
              disabled={isAnalyzing || isFixing}
            >
              <Zap className="h-4 w-4 mr-2" />
              Fix Corrupted ({report.corruptedCount})
            </Button>
          )}
        </div>
      </div>

      {report && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Overall Quality</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{report.overallScore}%</div>
              <Progress value={report.overallScore} className="mt-2" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Corrupted Modules</CardTitle>
              <FileWarning className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {report.corruptedCount || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Require regeneration
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Missing Audio</CardTitle>
              <Volume2 className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {report.missingAudioCount || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Need audio generation
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Modules</CardTitle>
              <Clock className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{report.totalModules}</div>
              <p className="text-xs text-muted-foreground">
                Across all courses
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {report && (
        <Tabs defaultValue="issues" className="w-full">
          <TabsList>
            <TabsTrigger value="issues">Corrupted Content</TabsTrigger>
            <TabsTrigger value="quality">Quality Metrics</TabsTrigger>
          </TabsList>

          <TabsContent value="issues" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Corrupted Modules ({report.corruptedCount})</CardTitle>
                <CardDescription>
                  Modules with AI refusal messages, placeholders, or undefined content
                </CardDescription>
              </CardHeader>
              <CardContent>
                {report.corruptedModules?.length > 0 ? (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {report.corruptedModules.slice(0, 20).map((module, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border rounded">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{module.courseId}</Badge>
                            <span className="font-mono text-sm">{module.moduleId}</span>
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-1">
                            {module.issue}
                          </p>
                        </div>
                        <Badge variant="destructive">Corrupted</Badge>
                      </div>
                    ))}
                    {report.corruptedModules.length > 20 && (
                      <p className="text-sm text-muted-foreground text-center py-2">
                        And {report.corruptedModules.length - 20} more...
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-green-600 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    No corrupted content found!
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="quality" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Quiz Coverage</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Modules with Quizzes</span>
                      <span>{report.quizCoverage}%</span>
                    </div>
                    <Progress value={report.quizCoverage} />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Content Completeness</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Healthy Modules</span>
                      <span>{report.contentCompleteness}%</span>
                    </div>
                    <Progress value={report.contentCompleteness} />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Healthy Content</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">
                    {report.healthyModules}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    modules with valid content
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Audio Coverage</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Modules with Audio</span>
                      <span>{report.totalModules > 0 ? Math.round(((report.totalModules - report.missingAudioCount) / report.totalModules) * 100) : 0}%</span>
                    </div>
                    <Progress value={report.totalModules > 0 ? ((report.totalModules - report.missingAudioCount) / report.totalModules) * 100 : 0} />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};